/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import PropTypes from 'prop-types';
import React, {PureComponent} from 'react';

import ContentWrapper from 'Component/ContentWrapper';
import Html from 'Component/Html';
import Image from 'Component/Image';
import TextPlaceholder from 'Component/TextPlaceholder';
import { HistoryType } from 'Type/Common';
import media, { WYSIWYG_MEDIA } from 'Util/Media';

import SpecialOffer from '../../component/Posts/SpecialOffer';
import BlogCategories from '../../component/BlogCategories';
import BlogRecentPosts from '../../component/BlogRecentPosts';
import BlogRelatedPosts from '../../component/BlogRelatedPosts';
import BlogRelatedProducts from '../../component/BlogRelatedProducts';
import BlogSearchBar from '../../component/BlogSearchBar';
import {convertDateFormat} from '../../util/Date';
import {POSTS_DETAILS,POSTS_TYPE_TWO,POSTS_TYPE_SPECIAL_OFFER,POSTS_TYPE_ONE} from './PostsDetails.config';

import './PostsDetails.style';


/**
 * @class PostsDetails
 * @namespace ScandiPWA/Blog/Route/PostsDetails/Component
 */
export class PostsDetails extends PureComponent {
    static propTypes = {
        requestPosts: PropTypes.func.isRequired,
        updateBreadcrumbs: PropTypes.func.isRequired,
        updateMeta: PropTypes.func.isRequired,
        history: HistoryType.isRequired,
        setHeaderState: PropTypes.func.isRequired,
        post: PropTypes.object.isRequired,
        match: PropTypes.shape({
            params: PropTypes.shape({
                handle: PropTypes.string.isRequired
            }).isRequired
        }).isRequired,
        removeRichdata: PropTypes.func.isRequired,
        addRichData: PropTypes.func.isRequired,
    };

    __construct(props) {
        super.__construct(props);

        this.options = {
            postOptions: {
                getDescription: true,
                getMedia: true,
                getRelated: true
            }
        };
    }

    state = {
        richLoaded: false
    }

    mountRichData(post) {
        const { addRichData } = this.props;

        const options = {
            name: "post",
            isExist: this.isExist(),
            post: post,
            logo: media('logo.svg', WYSIWYG_MEDIA),
        };
        addRichData(options);
        this.setState({ richLoaded: true });
    };

    unMountRichData() {
        const { removeRichdata } = this.props;
        removeRichdata(
            "post",
            this.isExist(),
        );
        this.setState({ richLoaded: false });
    }

    isExist = () => {
        if (document.getElementById("post")) {
            return true;
        }
        return false;
    };

    componentDidMount() {
        setTimeout(()=> {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 500);
        this.requestPosts();
        this.getPost();
        this.setHeaderState();
        this.storePathname();
    }

    componentDidUpdate() {
        const { post } = this.props;
        const { richLoaded } = this.state;

        this.updateBreadcrumbs();
        this.updatePage();
        this.updateMeta();
        if (typeof post === "object" && Object.keys(post).length > 0 && !richLoaded) {
            this.mountRichData(post);
        }
    }

    componentWillUnmount() {
        const { richLoaded } = this.state;
        if (richLoaded) {
            this.unMountRichData();
        }
    }

    setHeaderState() {
        const {setHeaderState, history} = this.props;
        setHeaderState({
            name: POSTS_DETAILS,
            title: 'Blog',
            onBackClick: () => history.goBack()
        });
    }

    updateMeta() {
        const {
            post: {
                canonical,
                title,
                meta_title,
                meta_description,
                meta_keyword,
                first_image,
                redirect_code_previous,
                og_image
            },
            updateMeta
        } = this.props;

        updateMeta({
            title,
            meta_title,
            meta_description,
            description: meta_description,
            meta_keyword,
            type: 'website',
            image: first_image || '',
            // canonical_url: this.getCanonicalUrl() || '',
            canonical_url: window.location.href,
            redirect_from: window.location.pathname,
            redirect_code_previous,
            og_image
        });
    }

    /**
     * Get url handle from router
     * @return {void | jsx}
     */
    getUrlParam() {
        const {match: {params: {handle}}} = this.props;
        return handle;
    }

    getPost() {
        const {post} = this.props;
        return post;
    }

    /**
     * Dispatch breadcrumbs update
     * @return {void}
     */
    updateBreadcrumbs() {
        const {updateBreadcrumbs, post, mfblog_permalink_route} = this.props;
        const {title, categories} = post;
        if (!title) {
            return;
        }
        let breadcrumbs = [];
        if(categories.length){
            let categoryTitle = categories[0].title;
            let categoryUrl = categories[0].canonical_url;
            breadcrumbs = [
                {
                    name: title
                },
                {
                    url: categoryUrl,
                    name: categoryTitle
                },
                {
                    url: `/${mfblog_permalink_route}`,
                    name: __('Blog')
                }
            ];
        }else {
            breadcrumbs = [
                {
                    name: title
                },
                {
                    url: `/${mfblog_permalink_route}`,
                    name: __('Blog')
                }
            ];
        }

        updateBreadcrumbs(breadcrumbs);
    }

    /**
     * Stores the old handle in state so that
     * it can compare it when a handle is changed
     */
    storePathname() {
        this.setState({oldHandle: this.getUrlParam()});
    }

    /**
     * Gets the canonical url for the specific post
     */
    getCanonicalUrl() {
        const { canonical_url } = this.props;

        if (!canonical_url) {
            return null;
        }

        return canonical_url;
    }

    /**
     * Updates the page if handle is changed
     */
    updatePage() {
        const {oldHandle} = this.state;

        if (oldHandle !== this.getUrlParam()) {
            this.requestPosts();
            this.getPost();
            this.setHeaderState();
            this.storePathname();
        }
    }

    requestPosts() {
        const {requestPosts, id} = this.props;
        const {postOptions} = this.options;

        requestPosts({
            ...postOptions,
            id
        });
    }

    renderTitle() {
        const {post: {title}} = this.props;

        return (
            <h1 mix={{block: 'PostsDetails', elem: 'Title'}}>
                <TextPlaceholder length="medium" content={title}/>
            </h1>
        );
    }

    /**
     * Renders the featured image
     * @returns {*}
     */
    renderImage() {
        const {post: {first_image, media_gallery, post_id}} = this.props;

        let imageUrl = first_image;

        if(media_gallery) {
            const mediaGalleryArray = media_gallery.split(';');

            if(mediaGalleryArray[0]){
                imageUrl = media(mediaGalleryArray[0]);
            }
        }

        if (!first_image && post_id) {
            return null;
        }

        return (
            <div
                block="PostsDetails"
                elem="MainImage"
            >
                <Image
                    block="PostsDetails"
                    elem="Image"
                    src={imageUrl}
                />
            </div>
        );
    }

    renderPublishDate() {
        const {post: {publish_time}} = this.props;

        if (!publish_time) {
            return null;
        }

        return (
            <div mix={{block: 'PostsDetails', elem: 'Date'}}>
                <TextPlaceholder
                    mix={{block: 'BlogRecentPosts', elem: 'DatePlaceholder'}}
                    content={convertDateFormat(publish_time)}
                />
            </div>
        );
    }

    renderAuthorName() {
        const {post: {author}} = this.props;

        if (!author) {
            return null;
        }

        return (
            <div mix={{block: 'PostsDetails', elem: 'Author'}}>
                {author.name}
            </div>
        );
    }

    renderContent() {
        const {post: {content}} = this.props;

        if (!content) {
            return (
                <TextPlaceholder
                    mix={{block: 'PostsDetails', elem: 'DescriptionPlaceholder'}}
                    length="custom"
                />
            );
        }

        return (
            <div mix={{block: 'PostsDetails', elem: 'Description'}}>
                <Html content={content}/>
            </div>

        );
    }

    render() {
        const {post: {media_gallery, post_type}} = this.props;

        if(post_type == POSTS_TYPE_ONE) {
            return (
                <main block="PostsDetails">
                    <ContentWrapper
                        wrapperMix={{block: 'PostsDetails', elem: 'Wrapper'}}
                        label="Post"
                    >
                        <div block="PostsDetails" elem="ColumnWrapper">
                            <div block="PostsDetails" elem="Content">
                                {this.renderContent()}
                            </div>
                            <BlogRelatedPosts/>
                            <BlogRelatedProducts/>
                        </div>
                    </ContentWrapper>
                </main>
            );
        }

        if(post_type == POSTS_TYPE_TWO) {
            return (
                <main block="PostsDetails">
                    <ContentWrapper
                        wrapperMix={{block: 'PostsDetails', elem: 'Wrapper'}}
                        label="Post"
                    >
                        <div block="PostsDetails" elem="ColumnWrapper">
                            {this.renderImage()}
                            <div block="PostsDetails" elem="Content">
                                {this.renderTitle()}
                                {this.renderContent()}
                            </div>
                            <BlogRelatedPosts/>
                            <BlogRelatedProducts/>
                        </div>
                    </ContentWrapper>
                </main>
            );
        }

        if(post_type == POSTS_TYPE_SPECIAL_OFFER){
            return <SpecialOffer { ...this.props }/>
        }

        return (
            <main block="PostsDetails">
                <ContentWrapper
                    wrapperMix={{block: 'PostsDetails', elem: 'Wrapper'}}
                    label="Post"
                >
                    <div block="PostsDetails" elem="ColumnWrapper">
                        {this.renderImage()}
                        <div block="PostsDetails" elem="Content">
                            {this.renderTitle()}
                            {this.renderContent()}
                        </div>
                        <BlogRelatedPosts/>
                        <BlogRelatedProducts/>
                    </div>
                </ContentWrapper>
            </main>
        );
    }
}

export default PostsDetails;
